import React from 'react';

import { FormattedMessage, type IntlShape, useIntl } from 'react-intl';

import {
	type SearchResultMessagePartial,
	type ThirdPartyUser,
} from '@atlassian/dynamic-cards-search-client';

import { i18n } from './messages';

const USER_DISPLAY_LIMIT = 3;
interface ThreadTitleProps {
	partial: SearchResultMessagePartial;
}

const dedupUsers = (
	userMap: Record<string, { index: number; thirdPartyUser: ThirdPartyUser }>,
	user: ThirdPartyUser,
	index: number,
) => {
	if (!userMap[user.id]) {
		userMap[user.id] = { thirdPartyUser: user, index };
	}
	return userMap;
};

const getTitleMessageValues = (users: (ThirdPartyUser | undefined)[] = [], intl: IntlShape) => {
	const { formatMessage } = intl;
	const uniqueUsers = users
		?.filter((thirdPartyUser): thirdPartyUser is ThirdPartyUser => Boolean(thirdPartyUser?.name))
		?.reduce(dedupUsers, {});
	const validContributors = Object.values(uniqueUsers)
		.sort((a, b) => a.index - b.index)
		.map((entry) => entry?.thirdPartyUser?.name || formatMessage(i18n.externalPersonLabel))
		.map((name) => name?.split(' ')[0]);

	const externalPersonLabel = formatMessage(i18n.externalPersonLabel);
	const externalPersonCount = validContributors.filter(
		(name) => name === externalPersonLabel,
	).length;

	let adjustedContributors = validContributors;
	if (externalPersonCount > 1) {
		adjustedContributors = validContributors.filter((name) => name !== externalPersonLabel);
		adjustedContributors.unshift(formatMessage(i18n.externalPeopleLabel));
	}

	const contributorCount = adjustedContributors.length;
	const namedContributors = adjustedContributors.slice(0, USER_DISPLAY_LIMIT);
	const overflowContributorCount = adjustedContributors.slice(USER_DISPLAY_LIMIT).length;

	return { contributorCount, namedContributors, overflowContributorCount };
};

export const ThreadTitle = ({ partial }: ThreadTitleProps): React.JSX.Element => {
	const intl = useIntl();
	const { formatMessage } = intl;
	let values = {};
	let titleMessage = i18n.groupChatTitle;

	if (
		partial?.entity?.container?.type === 'DIRECT_MESSAGE' ||
		partial?.entity?.container?.type === 'GROUP_DIRECT_MESSAGE'
	) {
		const groupChatParticipants = partial?.entity.container?.members?.map(
			(member) => member?.thirdPartyUser,
		);
		const { contributorCount, namedContributors, overflowContributorCount } = getTitleMessageValues(
			groupChatParticipants,
			intl,
		);
		const [name1, name2, name3] = namedContributors;

		values = {
			contributorCount,
			name1,
			name2,
			name3,
			overflowContributorCount,
		};
		titleMessage = i18n.groupChatTitle;
	} else {
		const linkedEntities = partial.linkedEntities || [];
		const threadUsers = [partial, ...linkedEntities].map(
			(linkedMessage) => linkedMessage?.entity?.createdBy?.thirdPartyUser,
		);
		const { contributorCount, namedContributors, overflowContributorCount } = getTitleMessageValues(
			threadUsers,
			intl,
		);

		const [name1, name2, name3] = namedContributors;
		const channelName =
			partial?.entity?.container?.displayName || formatMessage(i18n.unknownChannel);
		values = {
			contributorCount,
			name1,
			name2,
			name3,
			overflowContributorCount,
			channelName: channelName,
		};
		titleMessage = i18n.threadTitleWithOthers;
	}

	return <FormattedMessage {...titleMessage} values={values} />;
};
